import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import { EuiBasicTable, EuiButton, EuiButtonIcon, EuiCallOut, EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiLink, EuiModal, EuiModalBody, EuiModalHeader, EuiModalHeaderTitle, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiText, EuiTitle, EuiToolTip } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { CampaignApprovalQueueItem } from 'api/interfaces'
import { useGetCampaignApprovalQueueQuery } from 'api/rtkQueryApi/platform/campaignApprovalsApi'
import { useApproveCampaignMutation } from 'api/rtkQueryApi/platform/campaignsApi'
import { showErrorToast, showSuccessToast } from 'app/appSlice'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import { ActivateCampaignContent } from 'features/campaigns/campaign/ActivateCampaignContent'
import { DisapproveCampaignModal } from 'features/campaigns/campaign/DisapproveCampaignModal'

import { CampaignApprovalAdsPanel } from './CampaignApprovalAdsPanel'
import { CampaignApprovalDetailFlyout } from './CampaignApprovalDetailFlyout'
import { useOpenFromApprovalQueue } from './useOpenFromApprovalQueue'
import { extractApprovalErrorMessage, formatApprovalBudget, formatApprovalDate, formatApprovalDateRange, formatDeliveryDevices, formatDeliveryFrequency, formatDeliveryInventory, formatDeliveryTargetCpm, formatFootprintSummary } from './campaignApprovalFormatting'

export const CampaignApprovalQueuePage: React.FC = () => {
  const dispatch = useDispatch()
  const queueQuery = useGetCampaignApprovalQueueQuery()
  const [approveCampaign, approveCampaignResult] = useApproveCampaignMutation()
  const { openCampaign } = useOpenFromApprovalQueue()

  const [selectedItem, setSelectedItem] = useState<CampaignApprovalQueueItem | null>(null)
  const [confirmingItem, setConfirmingItem] = useState<CampaignApprovalQueueItem | null>(null)
  const [paymentItem, setPaymentItem] = useState<CampaignApprovalQueueItem | null>(null)
  const [disapprovingItem, setDisapprovingItem] = useState<CampaignApprovalQueueItem | null>(null)
  const [processingCampaignId, setProcessingCampaignId] = useState<string | null>(null)
  const [collapsedCampaignIds, setCollapsedCampaignIds] = useState<string[]>([])

  const isExpanded = (campaignId: string) => !collapsedCampaignIds.includes(campaignId)

  const toggleAds = (campaignId: string) => {
    setCollapsedCampaignIds(previous => (previous.includes(campaignId) ? previous.filter(id => id !== campaignId) : [...previous, campaignId]))
  }

  const onRequestApprove = (item: CampaignApprovalQueueItem) => {
    setConfirmingItem(item)
  }

  const onConfirmApprove = async () => {
    const item = confirmingItem
    if (!item) return
    setConfirmingItem(null)
    setProcessingCampaignId(item.campaignId)
    try {
      const result = await approveCampaign({ accountId: item.accountId, campaignId: item.campaignId }).unwrap()
      if (result.requiresPaymentActivation) {
        setPaymentItem(item)
      } else {
        dispatch(showSuccessToast('Campaign approved and activated'))
      }
      setSelectedItem(null)
    } catch (error) {
      dispatch(showErrorToast(extractApprovalErrorMessage(error, 'Failed to approve campaign')))
    } finally {
      setProcessingCampaignId(null)
    }
  }

  const onPaymentActivated = () => {
    setPaymentItem(null)
    dispatch(showSuccessToast('Campaign approved and activated'))
    queueQuery.refetch()
  }

  const onDisapproved = () => {
    setDisapprovingItem(null)
    setSelectedItem(null)
  }

  const singleLine: React.CSSProperties = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

  const columns: Array<EuiBasicTableColumn<CampaignApprovalQueueItem>> = [
    {
      name: '',
      width: '40px',
      isExpander: true,
      render: (item: CampaignApprovalQueueItem) => <EuiButtonIcon aria-label={isExpanded(item.campaignId) ? 'Hide ads' : 'Show ads'} iconType={isExpanded(item.campaignId) ? 'arrowDown' : 'arrowRight'} onClick={() => toggleAds(item.campaignId)} />
    },
    {
      name: 'Campaign',
      width: '22%',
      render: (item: CampaignApprovalQueueItem) => (
        <div style={{ minWidth: 0, wordBreak: 'break-word' }}>
          <EuiLink onClick={() => setSelectedItem(item)}>{item.campaignName}</EuiLink>
          <EuiText size='xs' color='subdued'>
            {item.accountName}
          </EuiText>
        </div>
      )
    },
    {
      name: 'Details',
      width: '18%',
      render: (item: CampaignApprovalQueueItem) => (
        <div>
          <EuiText size='s'>{item.campaignType}</EuiText>
          <EuiText size='s'>{formatApprovalBudget(item.budget, item.budgetFrequency)}</EuiText>
          <EuiText size='xs' color='subdued'>
            {formatApprovalDateRange(item.startDate, item.endDate)}
          </EuiText>
        </div>
      )
    },
    {
      name: 'Created',
      width: '14%',
      render: (item: CampaignApprovalQueueItem) => (
        <div style={{ minWidth: 0, wordBreak: 'break-word' }}>
          <EuiText size='s'>{formatApprovalDate(item.created)}</EuiText>
          <EuiText size='xs' color='subdued'>
            {item.createdBy}
          </EuiText>
        </div>
      )
    },
    {
      name: 'Audience',
      width: '18%',
      render: (item: CampaignApprovalQueueItem) => (
        <div style={{ minWidth: 0 }}>
          {item.uploadedAudienceUrl && item.uploadedAudienceFileName ? (
            <EuiToolTip content={item.uploadedAudienceFileName} position='top' display='block'>
              <EuiLink href={item.uploadedAudienceUrl} target='_blank' style={singleLine}>
                {item.uploadedAudienceFileName}
              </EuiLink>
            </EuiToolTip>
          ) : (
            <EuiText size='s'>No audience list</EuiText>
          )}
          <EuiText size='xs' color='subdued' style={singleLine}>
            Footprint: {formatFootprintSummary(item.audienceFootprint)}
          </EuiText>
        </div>
      )
    },
    {
      name: 'Delivery',
      width: '16%',
      render: (item: CampaignApprovalQueueItem) => (
        <div style={{ minWidth: 0 }}>
          <EuiText size='s'>{formatDeliveryFrequency(item.delivery)}</EuiText>
          <EuiText size='s'>{formatDeliveryTargetCpm(item.delivery)}</EuiText>
          <EuiText size='xs' color='subdued'>
            {formatDeliveryInventory(item.delivery)} / {formatDeliveryDevices(item.delivery)}
          </EuiText>
        </div>
      )
    },
    {
      name: 'Actions',
      width: '130px',
      render: (item: CampaignApprovalQueueItem) => (
        <EuiFlexGroup gutterSize='s' direction='column' responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiButton size='s' fill color='primary' onClick={() => onRequestApprove(item)} isLoading={approveCampaignResult.isLoading && processingCampaignId === item.campaignId}>
              Approve
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton size='s' color='danger' onClick={() => setDisapprovingItem(item)}>
              Disapprove
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      )
    }
  ]

  const items = queueQuery.data ?? []
  const itemIdToExpandedRowMap = items.filter(item => isExpanded(item.campaignId)).reduce<Record<string, React.ReactNode>>((map, item) => ({ ...map, [item.campaignId]: <CampaignApprovalAdsPanel item={item} /> }), {})

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>Campaign Approvals</h1>
            </EuiTitle>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false}>
          {queueQuery.isError && <EuiCallOut color='danger' iconType='alert' title={extractApprovalErrorMessage(queueQuery.error, 'Could not load the approval queue')} />}
          {!queueQuery.isLoading && !queueQuery.isError && items.length === 0 ? <EuiEmptyPrompt title={<h2>No campaigns waiting for approval</h2>} body={<p>Campaigns submitted for approval will show up here.</p>} /> : <EuiBasicTable items={items} columns={columns} loading={queueQuery.isLoading || queueQuery.isFetching} itemId='campaignId' itemIdToExpandedRowMap={itemIdToExpandedRowMap} isExpandable noItemsMessage='No campaigns waiting for approval' />}
        </EuiPanel>
      </EuiPageBody>
      {selectedItem && <CampaignApprovalDetailFlyout item={selectedItem} onClose={() => setSelectedItem(null)} onApprove={onRequestApprove} onDisapprove={setDisapprovingItem} onViewCampaign={openCampaign} isApproving={approveCampaignResult.isLoading && processingCampaignId === selectedItem.campaignId} />}
      {disapprovingItem && <DisapproveCampaignModal accountId={disapprovingItem.accountId} campaignId={disapprovingItem.campaignId} onClose={() => setDisapprovingItem(null)} onDisapproved={onDisapproved} />}
      {confirmingItem && <AcConfirmModal title='Approve campaign' message='Approving will activate this campaign. It will start running once its ads are approved.' onCancel={() => setConfirmingItem(null)} onConfirm={onConfirmApprove} cancelButtonText='Cancel' confirmButtonText='Approve' buttonColor='primary' />}
      {paymentItem && (
        <EuiModal onClose={() => setPaymentItem(null)}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>Activate {paymentItem.campaignName}</EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <ActivateCampaignContent campaignId={paymentItem.campaignId} amount={paymentItem.budget} campaignType={paymentItem.campaignType} onActivated={onPaymentActivated} />
          </EuiModalBody>
        </EuiModal>
      )}
    </EuiPage>
  )
}
